import { createContext, useContext } from 'react'

export const PLAYER_STORAGE_KEY = 'parrot_player_v2'

export const GAME_IDS = [
  'parrot-cart',
  'parrot-jump',
  'parrot-save',
  'parrots-vs-znakes',
] as const

export type GameId = typeof GAME_IDS[number]

export interface LeaderboardEntry {
  username:    string
  score:       number
  ts?:         number
}

export interface PlayerContextValue {
  /** X handle, null = not yet entered */
  username:     string | null
  /** Persist the X handle and close the gate */
  setUsername:  (u: string) => void
  /** Clear handle (logout) */
  clearUsername: () => void
  /**
   * Submit a score to the Redis-backed leaderboard.
   * Fire-and-forget — won't throw. Returns true if it was a new personal best.
   */
  submitScore:  (game: GameId, score: number) => Promise<boolean>
}

export const PlayerContext = createContext<PlayerContextValue>({
  username:     null,
  setUsername:  () => {},
  clearUsername: () => {},
  submitScore:  async () => false,
})

export function usePlayer(): PlayerContextValue {
  return useContext(PlayerContext)
}

// ─── API helpers ──────────────────────────────────────────────────────────────

export async function apiSubmitScore(
  game:     GameId,
  username: string,
  score:    number,
): Promise<{ ok: boolean; updated: boolean; personalBest: number }> {
  const res = await fetch('/api/submit', {   // ← matches api/submit.ts
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ game, username, score }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error ?? 'Submit failed')
  }

  return res.json()  // { ok, updated, personalBest }
}

export async function apiFetchLeaderboard(
  game: GameId | 'all',
): Promise<LeaderboardEntry[] | Record<string, LeaderboardEntry[]>> {
  const res = await fetch(`/api/leaderboard?game=${game}`)
  if (!res.ok) throw new Error('Failed to fetch leaderboard')
  const json = await res.json()
  return json.data  // { data: [...] } or { data: { 'parrot-cart': [...], ... } }
}